import { lazy, type ComponentType } from "react";

// Error shape for dynamic-import failures. `isChunkLoadError` is set by this
// module so the error boundary can distinguish a chunk-load failure from a
// real React render crash.
type ChunkLoadError = Error & { isChunkLoadError?: boolean };

type ComponentImport<T extends ComponentType<any>> = () => Promise<{
  default: T;
}>;

// Browser error messages for dynamic import failures vary by browser:
// Chrome:  "Failed to fetch dynamically imported module: <url>"
// Firefox: "error loading dynamically imported module"
// Safari:  "Importing a module script failed."
// Vite CSS preload: "Unable to preload CSS for /assets/<chunk>.css" (all browsers)
const CHUNK_ERROR_PATTERNS = [
  "Failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "Importing a module script failed",
  "Unable to preload CSS",
];

const isChunkLoadError = (error: ChunkLoadError) =>
  CHUNK_ERROR_PATTERNS.some((pattern) => error?.message?.includes(pattern));

// Prefer extracting the URL from the error message (Chrome/Firefox) so each
// unique failing chunk gets its own key. Fall back to the full message (Safari,
// which does not include the URL).
const getStorageKey = (error: ChunkLoadError) => {
  const urlMatch = error.message?.match(/https?:\/\/\S+/);
  const discriminator = urlMatch ? urlMatch[0] : (error.message ?? "unknown");
  return `retry-chunk-${discriminator}`;
};

// In-memory guard prevents double-retry within the same page lifecycle
// (e.g. two concurrent renders triggering the same failed import).
const refreshedChunks = new Set<string>();

const hasChunkBeenRefreshed = (key: string) => {
  if (refreshedChunks.has(key)) {
    return true;
  }
  try {
    return window.sessionStorage.getItem(key) === "true";
  } catch {
    // sessionStorage is unreadable — treat as already retried to prevent
    // an infinite reload loop (we cannot safely track state across reloads).
    return true;
  }
};

// Returns true only when the flag is successfully persisted to sessionStorage.
// If we cannot persist it we must NOT reload — without the flag surviving the
// reload we would loop forever.
const markChunkAsRefreshed = (key: string) => {
  refreshedChunks.add(key);
  try {
    window.sessionStorage.setItem(key, "true");
    return true;
  } catch {
    // Write failed (quota exceeded, private browsing, etc.) — skip reload.
    return false;
  }
};

const lazyRetry = <T extends ComponentType<any>>(
  componentImport: ComponentImport<T>
) =>
  componentImport().catch((error: ChunkLoadError) => {
    if (!isChunkLoadError(error)) {
      throw error;
    }

    const storageKey = getStorageKey(error);

    if (
      !hasChunkBeenRefreshed(storageKey) &&
      markChunkAsRefreshed(storageKey)
    ) {
      window.location.reload();
      // Stay pending while the reload happens — never resolve or reject,
      // so React keeps showing the Suspense fallback until the page reloads.
      return new Promise<{ default: T }>(() => {});
    }

    // Mark the error so the error boundary can distinguish it from a real
    // React render crash and log it as a regular Sentry error, not a boundary failure.
    error.isChunkLoadError = true;
    throw error;
  });

const lazyComponentLoader = <T extends ComponentType<any>>(
  lazyComponent: ComponentImport<T>
) => lazy(() => lazyRetry(lazyComponent));

export default lazyComponentLoader;
